import { orderTypeComparison } from '../data';

const spectrumPosition: Record<string, string> = {
  'Limit Order': 'left-[3%]',
  'Stop-Limit Order': 'left-[26%]',
  'Stop Order': 'left-[62%]',
  'Market Order': 'left-[93%]',
};

const riskColor = (risk: string) => {
  if (risk.includes('Zero')) return { bg: 'bg-secondary-500', text: 'text-secondary-700' };
  if (risk.includes('Capped')) return { bg: 'bg-primary-500', text: 'text-primary-700' };
  return { bg: 'bg-accent-500', text: 'text-accent-700' };
};

const fillGuaranteeIcon = (guarantee: string) => {
  if (guarantee.includes('Always')) return 'ri-check-double-fill text-secondary-600';
  if (guarantee.includes('not fill') || guarantee.includes('May not')) return 'ri-close-line text-accent-600';
  if (guarantee.includes('Fills at trigger')) return 'ri-arrow-right-line text-accent-600';
  return 'ri-question-line text-foreground-400';
};

const riskLabel = (risk: string) =>
  risk.replace(' (price-guaranteed)', '').replace(' (market fill after trigger)', '');

export default function SlippageOrderTypeSection() {
  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Editorial header — shorter */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Every order type has a price — either in pips or in missed trades
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            A market order costs you slippage. A limit order costs you missed fills. Understanding
            which trade-off matters more in each market condition is the real skill.
          </p>
        </div>

        {/* ── MOBILE SPECTRUM — vertical list ── */}
        <div className="sm:hidden mb-12">
          {/* Mini axis */}
          <div className="flex items-center justify-between mb-3 max-w-[680px] mx-auto">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-secondary-500 flex-shrink-0" />
              <span className="text-[11px] font-semibold text-secondary-700">Price certainty</span>
            </div>
            <span className="text-[11px] text-foreground-400">← spectrum →</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-accent-700">Execution certainty</span>
              <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0" />
            </div>
          </div>
          {/* Gradient bar */}
          <div className="relative max-w-[680px] mx-auto h-3 rounded-full bg-gradient-to-r from-secondary-400 via-primary-400 to-accent-400 mb-6" />
          {/* Vertical markers — ordered left-to-right visually */}
          <div className="max-w-[680px] mx-auto space-y-2.5">
            {/* Reorder to match visual left-to-right spectrum */}
            {[
              orderTypeComparison[1], // Limit
              orderTypeComparison[3], // Stop-Limit
              orderTypeComparison[2], // Stop
              orderTypeComparison[0], // Market
            ].map((o) => {
              const rc = riskColor(o.slippageRisk);
              return (
                <div key={o.label} className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${rc.bg} flex-shrink-0`} />
                  <span className="text-[13px] font-bold text-foreground-950">{o.label}</span>
                  <span className={`text-[11px] font-semibold ${rc.text}`}>{riskLabel(o.slippageRisk)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── DESKTOP SPECTRUM — full horizontal spectrum ── */}
        <div className="hidden sm:block relative mb-16 md:mb-20">
          {/* Axis labels */}
          <div className="flex items-center justify-between mb-6 max-w-[680px] mx-auto">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-secondary-500 flex-shrink-0" />
              <span className="text-[13px] font-semibold text-secondary-700">Price certainty</span>
            </div>
            <span className="text-[12px] text-foreground-400 hidden sm:inline">← trade-off spectrum →</span>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-accent-700">Execution certainty</span>
              <span className="w-2.5 h-2.5 rounded-full bg-accent-500 flex-shrink-0" />
            </div>
          </div>

          {/* The gradient bar */}
          <div className="relative max-w-[680px] mx-auto h-5 rounded-full bg-gradient-to-r from-secondary-400 via-primary-400 to-accent-400 overflow-hidden mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-400 via-primary-400 to-accent-400" />
          </div>

          {/* Positioned order type markers */}
          <div className="relative max-w-[680px] mx-auto h-[120px]">
            {orderTypeComparison.map((o) => (
              <div
                key={o.label}
                className={`absolute top-0 -translate-x-1/2 ${spectrumPosition[o.label] || 'left-1/2'} flex flex-col items-center`}
              >
                {/* Connector line */}
                <div className="w-px h-5 bg-foreground-300 mb-1.5" />
                {/* Marker dot */}
                <div className={`w-3.5 h-3.5 rounded-full border-2 border-background-100 ring-2 ring-background-100 ${riskColor(o.slippageRisk).bg} mb-2`} />
                {/* Order type name */}
                <span className="text-[14px] font-bold text-foreground-950 text-center leading-tight whitespace-nowrap">
                  {o.label}
                </span>
                {/* Risk tag */}
                <span className={`text-[11px] font-semibold mt-0.5 ${riskColor(o.slippageRisk).text}`}>
                  {riskLabel(o.slippageRisk)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Compact spec panels — 2×2 grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {orderTypeComparison.map((o) => {
            const rc = riskColor(o.slippageRisk);
            return (
              <div key={o.label} className="border-t-2 border-foreground-950 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <span className={`w-2 h-2 rounded-full ${rc.bg}`} />
                    <h3 className="text-[18px] font-[750] text-foreground-950 tracking-tight">{o.label}</h3>
                  </div>
                  <span className="text-[13px] font-semibold text-foreground-500">{o.slippageRate}</span>
                </div>

                {/* Core trade-off */}
                <p className="text-[14px] leading-[1.65] text-foreground-600 mb-4">
                  <span className="font-semibold text-foreground-800">How it works: </span>
                  {o.body}
                </p>

                {/* Specs row */}
                <div className="flex flex-wrap gap-x-8 gap-y-2 pt-4 border-t border-background-200">
                  <div className="flex items-center gap-2">
                    <i className={`${fillGuaranteeIcon(o.fillGuarantee)} text-[15px]`} />
                    <span className="text-[13px] font-semibold text-foreground-700">{o.fillGuarantee}</span>
                  </div>
                  <div>
                    <span className="text-[12px] font-semibold text-foreground-500 uppercase tracking-[0.08em]">Best for: </span>
                    <span className="text-[13px] text-foreground-600">{o.bestFor}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Paradox — slim banner */}
        <div className="mt-12 pt-6 border-t border-background-200 flex items-start gap-3 max-w-[740px]">
          <span className="text-[14px] font-bold text-foreground-400 leading-relaxed whitespace-nowrap">The paradox</span>
          <p className="text-[14px] leading-[1.7] text-foreground-500">
            The order type least likely to slip — a limit order — is also the least likely to fill when
            slippage conditions are worst. During a 50-pip NFP spike, a limit buy at 1.0850 sits
            untouched while the market trades at 1.0880. You saved 30 pips of slippage and missed a
            50-pip move. The right choice depends on whether your edge comes from <em>price</em> or{' '}
            <em>participation</em>.
          </p>
        </div>
      </div>
    </section>
  );
}